import { useState, useEffect, useContext } from "react";
import apiClient from "../../services/apiClient";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Collapse,
  Checkbox,
  Row,
  Col,
  message,
  Space,
  Divider,
} from "antd";
import {
  UserOutlined,
  CheckOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import "./AddUser.css";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const EditUser = () => {
  const [form] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Get user ID and email from location state or URL params
  const userId = location.state?.userId || location.pathname.split('/').pop();
  const userEmail = location.state?.email || "";
  const userRole = location.state?.role || "";

  // Fetch roles from API
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const res = await apiClient.get(`${baseUrl}/roles`, {
        headers: {
          "ngrok-skip-browser-warning": "application/json",
        },
      });

      if (!res.data.success) {
        throw new Error("Failed to fetch roles - success field is false");
      }

      setRoles(res.data.data);
    } catch (e) {
      setRoles([
        { id: 1, name: "Admin", description: "Full access to all features" },
        { id: 2, name: "User", description: "Standard user access" },
        {
          id: 3,
          name: "Manager",
          description: "Can manage users and view reports",
        },
        {
          id: 4,
          name: "Operation",
          description: "Handles operational tasks and workflows",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles on mount and set form values
  useEffect(() => {
    fetchRoles();
    // Set form values with email
    form.setFieldsValue({
      email: userEmail,
    });
  }, [userEmail]);

  // Set selected role when roles are loaded and userRole is available
  useEffect(() => {
    if (roles.length > 0 && userRole) {
      const roleObj = roles.find(r => r.name === userRole);
      if (roleObj) {
        setSelectedRole(roleObj.id);
      }
    }
  }, [roles, userRole]);

  // Handle form submit
  const handleSubmit = async () => {
    if (!selectedRole) {
      message.error("Please select a role");
      return;
    }

    const selected = roles.find((r) => r.id === selectedRole) || {};
    setLoading(true);

    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";

      const response = await apiClient.patch(
        `${baseUrl}/admin/user/${userId}/role`,
        {
          role: selected.name,
        }
      );

      if (response.data.success) {
        message.success("User role updated successfully!");
        navigate("/admin/users");
      } else {
        throw new Error(response.data.message || "Failed to update user role");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update user role.");
    } finally {
      setLoading(false);
    }
  };

  // Get policies for selected role
  const getRolePolicies = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    return role && role.policies ? role.policies : null;
  };



  return (
    <div className="add-user-container">
      <div
        className="add-user-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              borderRadius: 16,
            }}
          />
          
          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <Title
              level={1}
              style={{
                margin: 0,
                marginBottom: 12,
                fontWeight: 800,
                fontSize: 42,
                color: "#1a1a1a",
              }}
            >
              Update User Role
            </Title>
            <Text 
              style={{ 
                fontSize: 20, 
                color: "#4a4a4a",
                fontWeight: 400,
                lineHeight: 1.4,
              }}
            >
              Assign a new role to the user. User ID: {userId}
            </Text>
          </div>
        </div>
      </div>
      <Divider
        style={{
          margin: 0,
          marginBottom: 32,
          borderColor: isDark ? "#333c4a" : undefined,
        }}
      />
      <div className="form-container">
        <Row gutter={[32, 32]} align="stretch" style={{ display: "flex" }}>
          {/* Left Side: Form */}
          <Col
            xs={24}
            md={12}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Card
              className="form-card"
              style={{
                height: "600px",
                display: "flex",
                flexDirection: "column",
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                border: "1px solid #000",
                overflow: "hidden",
              }}
              bodyStyle={{
                borderRadius: 16,
                padding: "24px",
                height: "100%",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <div style={{ 
                flex: 1, 
                overflowY: "auto", 
                paddingRight: "8px", 
                marginRight: "-8px", 
                maxHeight: "calc(100% - 80px)" 
              }}>
                <Form
                  form={form}
                  layout="vertical"
                  className="add-user-form"
                >
                  <Form.Item
                    name="email"
                    label="Email Address"
                  >
                    <Input
                      placeholder="Email address"
                      size="large"
                      disabled
                      style={{
                        backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5',
                        color: isDark ? '#bfbfbf' : '#666'
                      }}
                    />
                  </Form.Item>

                  <div style={{ marginBottom: 24 }}>
                    <Title level={5} style={{ marginBottom: 8 }}>
                      Select Role
                    </Title>
                    <Space
                      direction="vertical"
                      style={{ width: "100%" }}
                      size="large"
                    >
                      {roles.map((role) => (
                        <div
                          key={role.id}
                          className={`role-option${selectedRole === role.id ? " selected" : ""
                            }`}
                          style={{
                            width: "100%",
                            cursor: "pointer",
                            border:
                              selectedRole === role.id
                                ? "2px solid #1890ff"
                                : "1px solid var(--admin-border)",
                            background:
                              selectedRole === role.id
                                ? isDark
                                  ? "#223a5f"
                                  : "#e6f7ff"
                                : undefined,
                            transition: "all 0.2s",
                          }}
                          onClick={() => setSelectedRole(role.id)}
                        >
                          <div className="role-header">
                            <span className="role-name">{role.name}</span>
                          </div>
                          <div className="role-description">
                            {role.description}
                          </div>
                        </div>
                      ))}
                    </Space>
                  </div>
                </Form>
              </div>
              
              {/* Submit Button - Fixed at bottom */}
              <div style={{ 
                padding: "16px 0 0 0", 
                borderTop: "1px solid #f0f0f0",
                marginTop: "16px"
              }}>
                <Space>
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate("/admin/users")}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckOutlined />}
                    loading={loading}
                    style={{ width: 180 }}
                    disabled={!selectedRole}
                    onClick={handleSubmit}
                  >
                    Update Role
                  </Button>
                </Space>
              </div>
            </Card>
          </Col>

          {/* Right Side: Permissions for Selected Role */}
          <Col
            xs={24}
            md={12}
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Card
              className="form-card"
              style={{
                height: "600px",
                display: "flex",
                flexDirection: "column",
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                border: "1px solid #000",
                overflow: "hidden",
              }}
              bodyStyle={{
                borderRadius: 16,
                padding: "24px",
                height: "100%",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <Title level={5} style={{ marginBottom: 16 }}>
                Permissions for Selected Role
              </Title>
              {selectedRole ? (
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    paddingRight: "8px",
                    marginRight: "-8px",
                    maxHeight: "calc(100% - 60px)"
                  }}
                  className="permissions-scrollable"
                >
                  {(() => {
                    const policies = getRolePolicies(selectedRole);
                    if (!policies || Object.keys(policies).length === 0) {
                      return (
                        <Text type="secondary">No permissions assigned.</Text>
                      );
                    }
                    return (
                      <Collapse defaultActiveKey={Object.keys(policies)}>
                        {Object.entries(policies).map(([group, policyList]) => (
                          <Panel
                            header={
                              group.charAt(0).toUpperCase() + group.slice(1)
                            }
                            key={group}
                          >
                            {policyList && policyList.length > 0 ? (
                              <Checkbox.Group
                                value={policyList.map((policy) => policy.id)}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 8,
                                }}
                              >
                                {policyList.map((policy) => (
                                  <Checkbox
                                    key={policy.id}
                                    value={policy.id}
                                    disabled
                                    style={{ marginBottom: 8 }}
                                  >
                                    <span
                                      style={{ color: "#888", fontSize: 13 }}
                                    >
                                      {policy.description}
                                    </span>
                                  </Checkbox>
                                ))}
                              </Checkbox.Group>
                            ) : (
                              <Text type="secondary">No permissions</Text>
                            )}
                          </Panel>
                        ))}
                      </Collapse>
                    );
                  })()}
                </div>
              ) : (
                <Text type="secondary">
                  Select a role to view its permissions.
                </Text>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default EditUser; 